import type { AdminAiDownRating } from '@steado/admin-contract/ai-ratings';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { StatCards, TabSkeleton } from '@/components/panel/panels';
import { ScopePills } from '@/components/table/ScopePills';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDuration } from '@/lib/format';
import { cacheHitRate, formatRate, formatTokens, formatUsd } from '@/lib/format-spend';
import { TabError } from '@/screens/user-detail/TabError';

import { CoveragePanel, PortionOffendersPanel } from './CoveragePanel';
import { MemoryPanel } from './MemoryPanel';
import { RatingsPanel } from './RatingsPanel';
import { TaskTable } from './TaskTable';
import { ThumbsDownList } from './ThumbsDownList';
import { WeeklyReviewPanel } from './WeeklyReviewPanel';
import { aiCoverageQueryOptions, aiMemoryQueryOptions, aiUsageQueryOptions } from './ai-ops-query';
import { AI_WINDOW_OPTIONS, type AiOpsSearch } from './ai-ops-search';
import { aiRatingsQueryOptions } from './ai-ratings-query';

export interface AiOpsScreenProps {
  readonly search: AiOpsSearch;
  readonly onSearchChange: (next: AiOpsSearch) => void;
  readonly onOpenRating: (row: AdminAiDownRating) => void;
}

/**
 * Independent queries, allowed to fail apart.
 *
 * They read different stores: Redis being unreachable costs the memory panel
 * its queue counts and leaves spend and coverage — both Postgres — working.
 * One combined endpoint would have made that a single failure for the whole
 * screen.
 */
export function AiOpsScreen({ search, onSearchChange, onOpenRating }: AiOpsScreenProps) {
  const { api } = useAuth();

  const usage = useQuery(aiUsageQueryOptions(api, search.days));
  const coverage = useQuery(aiCoverageQueryOptions(api, search.days));
  const memory = useQuery(aiMemoryQueryOptions(api, search.days));
  const ratings = useQuery(aiRatingsQueryOptions(api, search.days));

  const onSelectWindow = useCallback(
    (days: string) => {
      onSearchChange({ days: Number(days) });
    },
    [onSearchChange],
  );

  const totals = usage.data?.totals;

  return (
    <div className="flex max-w-[1240px] flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-[620px] text-[13px] leading-relaxed text-muted-foreground">
          Token and cost attribution per task, how users rated coach replies and weekly reviews, the food-scan
          cascade’s hit rate, and what memory extraction has written. No prompt or completion text is stored
          or shown. Those carry the user’s conditions and medications, which reach this console only through
          the audited health reveal.
        </p>

        <ScopePills
          label="Window"
          scopes={AI_WINDOW_OPTIONS.map((days) => ({ id: String(days), label: `${String(days)}d` }))}
          value={String(search.days)}
          onChange={onSelectWindow}
        />
      </div>

      {usage.isPending ? <TabSkeleton rows={3} /> : null}
      {usage.isError ? <TabError message={usage.error.message} onRetry={() => void usage.refetch()} /> : null}

      {totals === undefined || usage.data === undefined ? null : (
        <>
          <StatCards
            stats={[
              {
                label: `Model spend, ${String(usage.data.windowDays)} days`,
                value: formatUsd(totals.costUsd, totals.costIsPartial),
                /*
                 * The floor marker on the figure is not enough on its own:
                 * "≥" is easy to read past, and a total that is short by an
                 * unknown amount is worth a sentence.
                 */
                note: totals.costIsPartial
                  ? 'A floor, not a total — the provider did not price every call.'
                  : 'OpenRouter’s own figure, per call.',
              },
              {
                label: 'Calls',
                value: totals.calls.toLocaleString('en-IN'),
                note:
                  totals.failures === 0
                    ? 'None of them failed at the provider.'
                    : `${totals.failures.toLocaleString('en-IN')} failed at the provider.`,
                ...(totals.failures === 0 ? {} : { borderClassName: 'border-err-fg/25' }),
              },
              {
                label: 'Tokens',
                value: formatTokens(totals.inputTokens + totals.outputTokens),
                note: `${formatTokens(totals.inputTokens)} in, ${formatTokens(totals.outputTokens)} out`,
              },
              {
                label: 'Served from cache',
                value: formatRate(cacheHitRate(totals.cachedInputTokens, totals.inputTokens)),
                note: 'Prompt tokens the provider did not re-read. The breakpoint working.',
              },
              {
                // Mean, not median — the API averages, and calling it a median
                // would misdescribe a figure a long tail drags upward.
                label: 'Mean latency',
                value: formatDuration(totals.avgLatencyMs),
                note: 'Over successful calls only.',
              },
            ]}
          />

          <TaskTable usage={usage.data} />
          <WeeklyReviewPanel reviews={usage.data.weeklyReviews} windowDays={usage.data.windowDays} />
        </>
      )}

      {ratings.isPending ? <TabSkeleton rows={2} /> : null}
      {ratings.isError ? (
        <TabError message={ratings.error.message} onRetry={() => void ratings.refetch()} />
      ) : null}
      {ratings.data === undefined ? null : (
        <RatingsPanel ratings={ratings.data} weeklyReviews={usage.data?.weeklyReviews} />
      )}
      <ThumbsDownList key={search.days} days={search.days} onOpen={onOpenRating} />

      {coverage.isPending ? <TabSkeleton rows={2} /> : null}
      {coverage.isError ? (
        <TabError message={coverage.error.message} onRetry={() => void coverage.refetch()} />
      ) : null}
      {coverage.data === undefined ? null : (
        <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
          <CoveragePanel coverage={coverage.data} />
          <PortionOffendersPanel coverage={coverage.data} />
        </div>
      )}

      {/*
        Not in the imported design, and kept anyway: memory extraction is a
        live subsystem with a queue of its own, and this is the only screen
        that reads it. Dropping it to match a drawing would be the console
        losing a view, not gaining a layout.
      */}
      {memory.isPending ? <TabSkeleton rows={2} /> : null}
      {memory.isError ? (
        <TabError message={memory.error.message} onRetry={() => void memory.refetch()} />
      ) : null}
      {memory.data === undefined ? null : <MemoryPanel memory={memory.data} />}
    </div>
  );
}
